// Axios client for the standalone Jigyasa backend. Auth lives in httpOnly
// cookies, so every call carries credentials. On a 401 (expired access
// token) we silently try one refresh, then retry the original request.

import axios from "axios";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

let refreshing = null;

api.interceptors.response.use(undefined, async (error) => {
  const { config, response } = error;
  if (
    response?.status === 401 &&
    config &&
    !config._retried &&
    !config.url.includes("/auth/")
  ) {
    config._retried = true;
    refreshing =
      refreshing ||
      api.post("/auth/refresh").finally(() => {
        refreshing = null;
      });
    try {
      await refreshing;
      return api(config);
    } catch {
      // refresh failed — fall through to the original error
    }
  }
  throw error;
});

export const errText = (err) => {
  if (err?.response?.data?.detail) return err.response.data.detail;
  // No response object = the request never reached the server (network blip /
  // the preview server waking up), not an app error. Give a calm, clear nudge.
  if (err && !err.response) {
    return "Couldn't reach the server just now. Please wait a moment and try again.";
  }
  return "Something went wrong. Please try again.";
};

// Retry once on a pure network failure (no response). This smooths over the
// preview server briefly being unreachable. Only use for idempotent calls such
// as login — never for calls with side effects like sending an SMS.
export const postWithRetry = async (url, body, { retries = 1, delayMs = 1200 } = {}) => {
  try {
    return await api.post(url, body);
  } catch (err) {
    if (retries > 0 && err && !err.response) {
      await new Promise((r) => setTimeout(r, delayMs));
      return postWithRetry(url, body, { retries: retries - 1, delayMs });
    }
    throw err;
  }
};
